import * as XLSX from 'xlsx';

const asText = value => value == null ? '' : String(value);
const number = value => Number.isFinite(Number(value)) ? Number(value) : 0;
const statusName = value => ({RETURNED:'Devuelto',VOIDED:'Anulado',DELIVERED:'Entregado'}[String(value || '').toUpperCase()] || asText(value || 'Entregado'));
const dateValue = value => {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? asText(value) : d.toLocaleString('es-PE', {timeZone:'America/Lima',day:'2-digit',month:'2-digit',year:'numeric',hour:'2-digit',minute:'2-digit',second:'2-digit',hour12:false});
};
const safeName = name => asText(name).normalize('NFD').replace(/[\u0300-\u036f]/g,'').replace(/[^a-zA-Z0-9_-]+/g,'_').slice(0,48);
const summarize = (rows, field) => {
  const groups = new Map();
  for (const row of rows) {
    const key = asText(row[field] || 'Sin información');
    const current = groups.get(key) || {name:key, delivered:0, returned:0, voided:0};
    const qty = number(row.quantity);
    current.delivered += qty;
    if (row.status === 'RETURNED') current.returned += qty;
    if (row.status === 'VOIDED') current.voided += qty;
    groups.set(key,current);
  }
  return [...groups.values()].map(x => [x.name,x.delivered,x.returned,x.voided,x.delivered-x.returned-x.voided]);
};
const addSheet = (book, name, title, subtitle, headings, data, widths) => {
  const matrix = [
    ['MÓVILESPAY  |  INVENTARIO Y PROMOCIONES'],
    [title],
    [subtitle],
    ['Generado (hora Perú)',dateValue(new Date())],
    [],
    headings,
    ...data
  ];
  const sheet = XLSX.utils.aoa_to_sheet(matrix);
  sheet['!merges'] = [{s:{r:0,c:0},e:{r:0,c:headings.length-1}},{s:{r:1,c:0},e:{r:1,c:headings.length-1}},{s:{r:2,c:0},e:{r:2,c:headings.length-1}}];
  sheet['!cols'] = widths.map(w=>({wch:w}));
  sheet['!autofilter'] = {ref:XLSX.utils.encode_range({s:{r:5,c:0},e:{r:Math.max(5,matrix.length-1),c:headings.length-1}})};
  // SheetJS Community Edition retains data, merges, filters and widths; cell colors are not supported.
  XLSX.utils.book_append_sheet(book,sheet,name);
};

export function exportPromotionCampaignExcel(report, filters = {}) {
  if (!report?.campaign || !Array.isArray(report.rows)) throw new Error('El reporte no está disponible para exportar.');
  const {campaign,rows,summary={}}=report;
  const wb=XLSX.utils.book_new();
  const period = filters.from || filters.to ? `Período consultado: ${filters.from || 'Inicio'} al ${filters.to || 'Hoy'}` : 'Período consultado: todos los registros';
  const title = `CAMPAÑA: ${asText(campaign.name)}`;
  const delivered = number(summary.delivered),returned=number(summary.returned),voided=number(summary.voided),net=number(summary.net);
  addSheet(wb,'Resumen ejecutivo','REPORTE EJECUTIVO DE CAMPAÑA',title+'  |  '+period,
    ['Indicador','Unidades','Descripción'],[
      ['Campaña',asText(campaign.name),''],['Estado',asText(campaign.status),''],
      ['Inicio de vigencia',dateValue(campaign.starts_at),''],['Fin de vigencia',dateValue(campaign.ends_at),''],
      ['Entregados',delivered,'Unidades registradas como entregadas'],
      ['Devueltos',returned,'Unidades con devolución registrada'],
      ['Anulados',voided,'Unidades de operaciones anuladas'],
      ['Netos',net,'Entregados menos devueltos y anulados'],
      ['Movimientos',rows.length,'Cantidad de líneas de obsequios en el reporte'],
      ['Actualización del reporte',dateValue(report.updated_at),''],
      ['Observación','Los totales son unidades de producto, no ventas ni importes','']
    ],[32,30,70]);
  const details=rows.map((r,i)=>[
    i+1,dateValue(r.created_at),r.register_id ?? '',r.customer ?? '',r.dni ?? '',r.advisor ?? '',
    r.sede ?? '',r.issuer ?? '',r.document_number ?? '',r.product ?? '',r.category ?? '',
    r.supplier ?? '',number(r.quantity),statusName(r.status),r.id ?? ''
  ]);
  addSheet(wb,'Detalle de entregas','TRAZABILIDAD DE OBSEQUIOS',title+'  |  '+period,
    ['N.º','Fecha y hora','ID venta','Cliente','Documento cliente','Asesor','Sede','RUC / Empresa','Comprobante','Producto','Categoría','Proveedor','Cantidad','Estado','ID movimiento'],
    details.length?details:[['Sin movimientos en el período']],
    [7,23,13,34,18,30,29,33,23,34,20,30,12,16,16]);
  const exceptions=rows.filter(r=>['RETURNED','VOIDED'].includes(r.status)).map((r,i)=>[
    i+1,dateValue(r.created_at),r.register_id ?? '',r.customer ?? '',r.sede ?? '',r.product ?? '',
    number(r.quantity),statusName(r.status),r.id ?? ''
  ]);
  addSheet(wb,'Devoluciones y anulaciones','CONTROL DE DEVOLUCIONES Y ANULACIONES',title+'  |  '+period,
    ['N.º','Fecha del movimiento','ID venta','Cliente','Sede','Producto','Cantidad','Estado','ID movimiento'],
    exceptions.length?exceptions:[['Sin devoluciones ni anulaciones para los filtros aplicados']],
    [7,23,13,35,30,35,12,18,17]);
  const sections=[['PRODUCTOS','product'],['SEDES','sede'],['ASESORES','advisor'],['RUC / EMPRESAS','issuer']];
  const analysis=[];
  for(const [label,field] of sections){
    analysis.push([label,'','','','']);
    analysis.push(...summarize(rows,field));
    analysis.push(['','','','','']);
  }
  addSheet(wb,'Análisis por sede y producto','ANÁLISIS DE DISTRIBUCIÓN',title+'  |  '+period,
    ['Agrupación','Entregados','Devueltos','Anulados','Netos'],analysis.length?analysis:[['Sin datos']],
    [42,18,18,18,18]);
  XLSX.writeFile(wb,`MOVILESPAY_CAMPANA_${safeName(campaign.name)}_${new Date().toISOString().slice(0,10).replace(/-/g,'')}.xlsx`,{bookType:'xlsx',compression:true});
}
